import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import analytics from 'js/analytics';
import useBreakpoints from 'hooks/use-breakpoints';
import Modal from 'components/modal';
import ContentContainer from 'components/content-container';
import Form from 'components/form/form';
import TextInput from 'components/text-input/text-input';
import Button from 'components/button';
import HiddenInput from 'components/hidden-input';
import PageSpinner from 'components/page-spinner';

const NotifyMeModal = ({
  buttonLabel,
  productName,
  productCategory,
  productBrand,
  outOfStockText,
  emailInputLabel,
  emailInputPlaceholder,
  cancelLabel,
  form,
  itemIdFieldNameForAnalytics,
  closeLabel,
  isOpen,
  setIsOpen,
  selectedProductValues
}) => {
  const { isTablet } = useBreakpoints(true, true);
  const [responseMessage, setResponseMessage] = useState('');
  const [formKey, setFormKey] = useState(0);
  const ModalContentWrapper = isTablet ? 'div' : ContentContainer;
  const modalId = 'notify-me-modal';

  useEffect(() => {
    if (!isOpen) {
      setResponseMessage('');
      setFormKey(prevKey => prevKey + 1);
    }
  }, [isOpen]);

  const onResponse = response => {
    setResponseMessage(response.messageToUser);

    if (response.isRegistered) {
      const eventData = {
        event: 'stock_notification',
        items: [
          {
            item_id: selectedProductValues[itemIdFieldNameForAnalytics],
            item_name: productName,
            item_category: productCategory,
            item_brand: productBrand
          }
        ]
      };

      analytics.send(eventData);
    }
  };

  return (
    <Modal
      animate={false}
      className="notify-me-modal"
      containerClassName="notify-me-modal__inner"
      contentClassName="notify-me-modal__content"
      id={modalId}
      isVisible={isOpen}
      hide={() => setIsOpen(false)}
      closeButtonText={closeLabel}
      closeButtonClassName="notify-me-modal__close-btn"
      hideCloseText={false}
    >
      <ModalContentWrapper>
        <h2 className="notify-me-modal__title">{productName}</h2>
        {outOfStockText && (
          <span className="notify-me-modal__out-of-stock-text">
            {outOfStockText}
          </span>
        )}
        <div className="notify-me-modal__content-wrapper">
          {responseMessage ? (
            <p className="notify-me-modal__response-message">
              {responseMessage}
            </p>
          ) : (
            form && (
              <Form
                {...form}
                key={formKey}
                fieldNameMapping={{ 'notify-me-email': 'email' }}
                onResponse={onResponse}
                className="notify-me-modal__form"
              >
                {({ isLoading }) => (
                  <>
                    <PageSpinner
                      shouldContainInParent={true}
                      isLoading={isLoading}
                    />
                    {selectedProductValues &&
                      Object.entries(selectedProductValues).map(
                        ([name, value]) => (
                          <HiddenInput key={name} name={name} value={value} />
                        )
                      )}
                    <div className="notify-me-modal__form-content">
                      <TextInput
                        validations={['isRequired', 'isEmail']}
                        type="email"
                        autocomplete="email"
                        name="notify-me-email"
                        label={emailInputLabel}
                        placeholder={emailInputPlaceholder}
                        theme={TextInput.themes.bordered}
                        className="notify-me-modal__email-input"
                      />
                    </div>
                    <div className="notify-me-modal__actions">
                      <Button
                        type="submit"
                        text={buttonLabel}
                        className="notify-me-modal__action-btn"
                        themes={[Button.themes.buttonGray]}
                      />
                      <Button
                        onClick={() => setIsOpen(false)}
                        text={cancelLabel}
                        className="notify-me-modal__action-btn"
                        themes={[Button.themes.buttonOutline]}
                      />
                    </div>
                  </>
                )}
              </Form>
            )
          )}
        </div>
      </ModalContentWrapper>
    </Modal>
  );
};

NotifyMeModal.propTypes = {
  buttonLabel: PropTypes.string,
  productName: PropTypes.string,
  productCategory: PropTypes.string,
  productBrand: PropTypes.string,
  outOfStockText: PropTypes.string,
  emailInputLabel: PropTypes.string,
  emailInputPlaceholder: PropTypes.string,
  form: PropTypes.exact(Form.propTypes),
  cancelLabel: PropTypes.string,
  closeLabel: PropTypes.string,
  itemIdFieldNameForAnalytics: PropTypes.string,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  selectedProductValues: PropTypes.object
};

NotifyMeModal.propTypesMeta = {
  productName: 'exclude',
  productCategory: 'exclude',
  productBrand: 'exclude',
  isOpen: 'exclude',
  setIsOpen: 'exclude',
  selectedProductValues: 'exclude'
};

export default NotifyMeModal;
